import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { X, Palette, Package, Image as ImageIcon, ArrowLeft, Download, MessageCircle, Upload, Loader2, Edit2, Plus, Trash2, Save, Sparkles, Eye, ChevronDown, Tag, Wand2 } from 'lucide-react';
import { getKitsByFabricImage, getKitsByFabricImagePublic } from '../api/kit.api';
import { createPublicCatalogue } from '../api';
import { updateProduct, extractColorVariants } from '../api/product.api';
import { createFabricVendorConnectRequest } from '../api/fabricVendorConnectRequest.api';
import { generateMultiConceptImage } from '../api/realtime.api';
import { useBackButton } from '../hooks/useBackButton';
import { uploadImageToS3, compressImage } from '../utils/s3Upload';
import ColorVariantsModal from './ColorVariantsModal';
import { toThumbnailUrl } from '../utils/imageThumbnailUrl';
import {
  FABRIC_VISUAL_TYPE_OPTIONS,
  WEAVE_OR_KNIT_OPTIONS,
  FIBER_OPTIONS,
  OPACITY_OPTIONS,
  STRETCH_OPTIONS,
  GSM_WEIGHT_OPTIONS
} from '../data/fabricAnalysisOptions';
import { FABRIC_TEXTURES } from '../data/fabricTextures';
import TextureSelector from './TextureSelector';
import FabricGarmentInsights, {
  hasGarmentTypeInsights,
  getGarmentTypeInsights,
  GARMENT_INSIGHT_AUDIENCES,
} from './FabricGarmentInsights';

const MAX_TAGS_PER_FABRIC = 30;

const getShadeCardsFromFabric = (fabric) => {
  if (!fabric) return [];
  if (fabric.shadeCards && Array.isArray(fabric.shadeCards)) return fabric.shadeCards;
  if (fabric.shadeCard) return [fabric.shadeCard];
  return [];
};

const fabricVisualFingerprint = (fabric) => {
  if (!fabric) return '';
  const id = fabric._id || fabric.id || '';
  const shadeUrls = getShadeCardsFromFabric(fabric).map((sc) =>
    typeof sc === 'string' ? sc : sc?.url
  ).filter(Boolean);
  const colorHexes = (fabric.colorVariants || []).map((v) => v?.hex || v?.color).filter(Boolean);
  return `${id}|${shadeUrls.join(',')}|${colorHexes.join(',')}`;
};

const FabricCatalogueView = ({
  fabric,
  onClose,
  showNotification,
  onFabricUpdated,
  allowShadeCardUpload = true,
  liteMode = false,
  setProductDetails,
  setPreSelectedProduct,
  setActiveView,
  onOpenLoginModal,
  onGenerateFabric,
  openAiStudio
}) => {
  useBackButton(true, () => { if (onClose) onClose(); });
  const [kits, setKits] = useState([]);
  const [isLoadingKits, setIsLoadingKits] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(-1);
  const [allViewableImages, setAllViewableImages] = useState([]);
  const [brokenImages, setBrokenImages] = useState(new Set());
  const [isSharingWhatsApp, setIsSharingWhatsApp] = useState(false);
  const [isUploadingShadeCard, setIsUploadingShadeCard] = useState(false);
  const [isGeneratingMultiConcept, setIsGeneratingMultiConcept] = useState(false);
  const [multiConceptResult, setMultiConceptResult] = useState(null);
  const [localFabric, setLocalFabric] = useState(fabric);
  const [isEditingProperties, setIsEditingProperties] = useState(false);
  const [editingProperties, setEditingProperties] = useState([]);
  const [isSavingProperties, setIsSavingProperties] = useState(false);
  const [isEditingTags, setIsEditingTags] = useState(false);
  const [editingTags, setEditingTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const shadeCardInputRef = useRef(null);
  const contentScrollRef = useRef(null);
  const [isEditingColors, setIsEditingColors] = useState(false);
  const [editingColorVariants, setEditingColorVariants] = useState([]);
  const [isSavingColors, setIsSavingColors] = useState(false);
  const [isEditingFabricAnalysis, setIsEditingFabricAnalysis] = useState(false);
  const [editingFabricAnalysis, setEditingFabricAnalysis] = useState(null);
  const [isSavingFabricAnalysis, setIsSavingFabricAnalysis] = useState(false);
  const [colorVariantsModalOpen, setColorVariantsModalOpen] = useState(false);
  const [selectedKitForColorVariants, setSelectedKitForColorVariants] = useState(null);
  const prevFabricIdRef = useRef(fabric?._id || fabric?.id || null);
  const fabricVisualFpRef = useRef(fabricVisualFingerprint(fabric));
  const brokenImagesRef = useRef(new Set());
  const [showConnectVendorModal, setShowConnectVendorModal] = useState(false);
  const [connectRequesterName, setConnectRequesterName] = useState('');
  const [connectMobileNumber, setConnectMobileNumber] = useState('');
  const [isSubmittingVendorConnect, setIsSubmittingVendorConnect] = useState(false);

  const fabricVisualFp = useMemo(() => fabricVisualFingerprint(fabric), [fabric]);

  // Sync local fabric when prop identity or visual data (shade cards / colors) changes
  useEffect(() => {
    if (!fabric) {
      const currentFabricId = prevFabricIdRef.current;
      if (currentFabricId !== null) {
        setLocalFabric(null);
        prevFabricIdRef.current = null;
        fabricVisualFpRef.current = '';
      }
      return;
    }

    const fabricId = fabric._id || fabric.id;
    const prevFabricId = prevFabricIdRef.current;
    const visualChanged = fabricVisualFp !== fabricVisualFpRef.current;

    if (fabricId !== prevFabricId) {
      setLocalFabric(fabric);
      setEditingProperties(fabric.properties || []);
      setEditingTags(Array.isArray(fabric.tags) ? [...fabric.tags] : []);
      setIsEditingTags(false);
      setTagInput('');
      prevFabricIdRef.current = fabricId;
      fabricVisualFpRef.current = fabricVisualFp;
      brokenImagesRef.current = new Set();
      setBrokenImages(new Set());
      return;
    }

    if (visualChanged) {
      fabricVisualFpRef.current = fabricVisualFp;
      setLocalFabric((prev) => {
        if (!prev) return fabric;
        return {
          ...prev,
          ...fabric,
          shadeCards: fabric.shadeCards,
          shadeCard: fabric.shadeCard,
          colorVariants: fabric.colorVariants
        };
      });
    }
  }, [fabricVisualFp, fabric?._id, fabric?.id]);
  
  const colorVariantsFingerprint = useMemo(
    () =>
      JSON.stringify(
        (localFabric?.colorVariants || []).map((v) => `${v?.hex || v?.color || ''}|${v?.colorName || v?.name || ''}`)
      ),
    [localFabric?.colorVariants]
  );

  // Normalize color variants to ensure all have names (for display purposes)
  const normalizedColorVariants = useMemo(() => {
    if (!localFabric?.colorVariants || !Array.isArray(localFabric.colorVariants)) {
      return [];
    }

    return localFabric.colorVariants.map((variant, index) => {
      const colorName = variant.colorName || variant.name || `Color ${index + 1}`;
      return {
        ...variant,
        colorName
      };
    });
  }, [colorVariantsFingerprint]);

  // Get current user ID from token and check admin role
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Decode JWT token to get user ID
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(payload.userId);
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
    
    // Check if user is admin
    const userRole = localStorage.getItem('role')?.toLowerCase();
    setIsAdmin(userRole === 'admin');
  }, []);

  // Check if current user is the owner
  const isOwner = useMemo(() => {
    if (!currentUserId || !localFabric.user) return false;
    // Handle both string ID and object ID
    const fabricUserId = typeof localFabric.user === 'object' ? localFabric.user._id : localFabric.user;
    return fabricUserId === currentUserId || fabricUserId?.toString() === currentUserId?.toString();
  }, [currentUserId, localFabric.user]);
  
  // Check if user can edit (owner or admin)
  const canEditColors = !liteMode && (isOwner || isAdmin);
  const canEditFabricAnalysis = !liteMode && (isOwner || isAdmin);
  const canEditTags = !liteMode && (isOwner || isAdmin);
  const canEditProperties = !liteMode && (isOwner || isAdmin);
  const canUploadShadeCards = allowShadeCardUpload && !liteMode;

  const resolveFabricId = useCallback(() => {
    return localFabric?._id || localFabric?.id || null;
  }, [localFabric?._id, localFabric?.id]);

  const resolveVendorId = useCallback(() => {
    if (!localFabric?.user) return null;
    return typeof localFabric.user === 'object' ? localFabric.user._id : localFabric.user;
  }, [localFabric?.user]);

  const openVendorConnectModal = useCallback(() => {
    setConnectRequesterName('');
    setConnectMobileNumber('');
    setShowConnectVendorModal(true);
  }, []);

  const submitVendorConnectRequest = useCallback(
    async ({ requesterName, mobileNumber } = {}) => {
      const fabricId = resolveFabricId();
      const vendorId = resolveVendorId();
      if (!fabricId || !vendorId) {
        showNotification?.('Unable to submit request.', 'error');
        return;
      }

      setIsSubmittingVendorConnect(true);
      try {
        await createFabricVendorConnectRequest({
          fabricId,
          vendorId,
          requesterName,
          mobileNumber
        });
        showNotification?.('Request submitted. Vendor will contact you soon.', 'success');
        setShowConnectVendorModal(false);
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to submit request';

        // If backend needs a mobile number, fallback to modal
        if (
          String(message).toLowerCase().includes('mobile') &&
          !showConnectVendorModal &&
          !mobileNumber
        ) {
          openVendorConnectModal();
        } else {
          showNotification?.(message, 'error');
        }
      } finally {
        setIsSubmittingVendorConnect(false);
      }
    },
    [openVendorConnectModal, resolveFabricId, resolveVendorId, showConnectVendorModal, showNotification]
  );

  const handleConnectWithVendor = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      openVendorConnectModal();
      return;
    }
    // Logged in: submit without asking; backend uses req.user
    submitVendorConnectRequest();
  }, [openVendorConnectModal, submitVendorConnectRequest]);

  // Memoize fabricImage to prevent unnecessary re-renders
  const fabricImage = useMemo(() => {
    return localFabric?.image || localFabric?.images?.original?.s3Url || localFabric?.images?.original?.imgbbUrl || localFabric?.images?.original?.url;
  }, [localFabric?.image, localFabric?.images?.original?.s3Url, localFabric?.images?.original?.imgbbUrl, localFabric?.images?.original?.url]);
  
  const variantCount = localFabric?.colorVariants ? localFabric.colorVariants.length : 0;
  
  const shadeCards = useMemo(() => getShadeCardsFromFabric(localFabric), [localFabric]);
  const preventContextMenu = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleKitCardClick = useCallback(
    (kit, kitImage) => {
      if (openAiStudio) {
        openAiStudio(kit, { returnTo: { type: 'fabric', fabric: localFabric } });
        return;
      }
      if (kitImage) {
        handleImageClick(kitImage);
      }
    },
    [openAiStudio, localFabric]
  );

  const imageInteractionProps = useMemo(
    () => ({
      draggable: false,
      onContextMenu: preventContextMenu,
      style: {
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        WebkitUserDrag: 'none',
        userSelect: 'none'
      }
    }),
    [preventContextMenu]
  );

  // Debug logging - only log when fabric ID changes to avoid infinite loops
  useEffect(() => {
    if (!localFabric?._id) return;
    console.log('📦 Fabric Catalogue Data:', {
      fabricName: localFabric.name,
      fabricImage: fabricImage,
      shadeCardsCount: shadeCards.length,
      shadeCards: shadeCards,
      colorVariantsCount: localFabric.colorVariants?.length || 0
    });
  }, [localFabric?._id]); // Only depend on fabric ID, not the entire object

  // Handle WhatsApp contact with vendor
  const handleContactVendor = useCallback(() => {
    try {
      const vendor = localFabric.user;
      if (!vendor) {
        if (showNotification) {
          showNotification('Vendor information not available', 'error');
        }
        return;
      }

      const vendorPhone = typeof vendor === 'object' ? vendor.phone : null;
      if (!vendorPhone) {
        if (showNotification) {
          showNotification('Vendor phone number not available', 'error');
        }
        return;
      }

      // Normalize phone number (remove non-digits)
      const normalizedPhone = vendorPhone.replace(/\D/g, '');
      // Add country code if not present (assuming India +91)
      const phoneWithCountryCode = normalizedPhone.startsWith('91') 
        ? normalizedPhone 
        : `91${normalizedPhone}`;

      // Create WhatsApp message
      const vendorName = typeof vendor === 'object' ? vendor.name : 'Vendor';
      const fabricName = localFabric.name || 'this fabric';
      const message = `Hi ${vendorName}, I'm interested in ${fabricName}. Could you please share more details?`;

      // Include fabric image if available
      const fabricImg = fabricImage || localFabric.images?.original?.s3Url || localFabric.images?.original?.url || localFabric.image;
      let whatsappMessage = message;
      if (fabricImg) {
        whatsappMessage = `${message}\n\nFabric Image: ${fabricImg}`;
      }

      // Open WhatsApp
      const whatsappUrl = `https://wa.me/${phoneWithCountryCode}?text=${encodeURIComponent(whatsappMessage)}`;
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening WhatsApp:', error);
      if (showNotification) {
        showNotification('Failed to open WhatsApp', 'error');
      }
    }
  }, [localFabric, fabricImage, showNotification]);

  const handleImageError = useCallback((imageUrl) => {
    if (!imageUrl || brokenImagesRef.current.has(imageUrl)) return;
    brokenImagesRef.current.add(imageUrl);
    setBrokenImages(new Set(brokenImagesRef.current));
  }, []);

  const isImageBroken = (imageUrl) => brokenImages.has(imageUrl);

  const handleWhatsAppShare = async () => {
    if (!kits || kits.length === 0) {
      if (showNotification) {
        showNotification('No kits available to share. Generate kits from this fabric first.', 'warning');
      }
      return;
    }

    try {
      setIsSharingWhatsApp(true);
      const token = localStorage.getItem('token');
      const title = `${localFabric.name} - Fabric Catalogue`;
      
      // Create public catalogue with all kits from this fabric
      const response = await createPublicCatalogue({
        title,
        description: `Complete catalogue for ${localFabric.name} with ${kits.length} kit${kits.length > 1 ? 's' : ''}, ${variantCount} color variant${variantCount !== 1 ? 's' : ''}, and ${shadeCards.length} shade card${shadeCards.length !== 1 ? 's' : ''}`,
        kitIds: kits.map(k => k._id),
        productId: localFabric._id, // Include fabric/product ID for direct lookup
        settings: {
          allowDownloads: true,
          showKitNames: true,
          showKitDescriptions: true,
          showBrandInfo: true
        }
      }, token);

      const data = response?.data?.data;
      const publicUrl = data?.publicUrl || `${window.location.origin}/public-catalogue/${data?.publicCatalogue?.publicId || data?.publicId}`;
      
      if (!publicUrl) {
        throw new Error('Failed to get public link');
      }

      const message = `🎨 *${fabric.name}*\n\n${publicUrl}`;
      
      // Use location navigation to avoid popup blockers on iOS/Safari
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      const shareUrl = isIOS
        ? `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`
        : `https://wa.me/?text=${encodeURIComponent(message)}`;
      
      window.location.href = shareUrl;
      
      if (showNotification) {
        showNotification('Opening WhatsApp...', 'success');
      }
    } catch (err) {
      console.error('WhatsApp share failed:', err);
      if (showNotification) {
        showNotification(err?.response?.data?.message || err?.message || 'Failed to share on WhatsApp', 'error');
      }
    } finally {
      setIsSharingWhatsApp(false);
    }
  };

  useEffect(() => {
    const fetchKits = async () => {
      if (!fabricImage) {
        setIsLoadingKits(false);
        return;
      }

      setIsLoadingKits(true);
      try {
        console.log('🔍 Fetching kits for fabric image:', fabricImage);
        
        // Check if user is logged in
        const token = localStorage.getItem('token');
        const fabricKits = token 
          ? await getKitsByFabricImage(fabricImage)
          : await getKitsByFabricImagePublic(fabricImage);
        
        console.log(`✅ Found ${fabricKits.length} kits for this fabric`);
        
        setKits(fabricKits);
      } catch (error) {
        console.error('❌ Error fetching kits:', error);
        if (showNotification) {
          showNotification('Failed to load kits for this fabric', 'error');
        }
        setKits([]); // Set empty array on error
      } finally {
        setIsLoadingKits(false);
      }
    };

    fetchKits();
  }, [fabricImage, showNotification]);

  const allViewableImagesList = useMemo(() => {
    const images = [];
    if (fabricImage) {
      images.push({ url: fabricImage, type: 'fabric', index: 0 });
    }
    shadeCards.forEach((shadeCard, idx) => {
      const shadeCardUrl = typeof shadeCard === 'string' ? shadeCard : shadeCard.url;
      if (shadeCardUrl) {
        images.push({ url: shadeCardUrl, type: 'shadeCard', index: idx + 1 });
      }
    });
    return images;
  }, [fabricImage, shadeCards]);

  useEffect(() => {
    setAllViewableImages(allViewableImagesList);
  }, [allViewableImagesList]);

  const handleImageClick = (imageUrl) => {
    const imageIndex = allViewableImages.findIndex(img => img.url === imageUrl);
    if (imageIndex !== -1) {
      setSelectedImageIndex(imageIndex);
      setSelectedImage(imageUrl);
    } else {
      setSelectedImage(imageUrl);
      setSelectedImageIndex(-1);
    }
  };

  const handleNextImage = () => {
    if (selectedImageIndex >= 0 && selectedImageIndex < allViewableImages.length - 1) {
      const nextIndex = selectedImageIndex + 1;
      const nextImage = allViewableImages[nextIndex];
      setSelectedImageIndex(nextIndex);
      setSelectedImage(nextImage.url);
    }
  };

  const handlePreviousImage = () => {
    if (selectedImageIndex > 0) {
      const prevIndex = selectedImageIndex - 1;
      const prevImage = allViewableImages[prevIndex];
      setSelectedImageIndex(prevIndex);
      setSelectedImage(prevImage.url);
    }
  };

  const handleCloseImage = () => {
    setSelectedImage(null);
    setSelectedImageIndex(-1);
  };

  // Keyboard navigation for image viewer
  useEffect(() => {
    if (!selectedImage) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleCloseImage();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNextImage();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePreviousImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, selectedImageIndex, allViewableImages]);

  const handleShadeCardUpload = async (file) => {
    if (!canUploadShadeCards) return;
    if (!file || !localFabric._id) return;

    try {
      setIsUploadingShadeCard(true);
      
      // Compress and upload shade card to S3
      const compressed = await compressImage(file, { maxWidth: 1600, maxHeight: 1600, quality: 0.8 });
      const uploadRes = await uploadImageToS3(compressed, {
        folder: 'shadecards',
        fileName: `shadecard_${Date.now()}_${compressed.name}`
      });

      // Extract colors from shade card
      const apiRes = await extractColorVariants(uploadRes.url, 100);
      const palette = apiRes.palette || apiRes.data?.palette || [];

      // Get existing shade cards and color variants
      const existingShadeCards = localFabric.shadeCards || (localFabric.shadeCard ? [localFabric.shadeCard] : []);
      const existingColorVariants = localFabric.colorVariants || [];

      // Add new shade card
      const newShadeCards = [
        ...existingShadeCards.map(sc => typeof sc === 'string' ? { url: sc } : sc),
        { url: uploadRes.url }
      ];

      // Merge new colors with existing ones (avoid duplicates by hex value)
      const existingHexes = new Set(existingColorVariants.map(v => (v.hex || '').toLowerCase()));
      const newColors = Array.isArray(palette) ? palette
        .filter(c => !existingHexes.has((c.hex || '').toLowerCase()))
        .map((c, idx) => ({
          colorName: c.name || `Color ${existingColorVariants.length + idx + 1}`,
          hex: c.hex || '#000000'
        })) : [];
      
      // Normalize existing colors to ensure they all have names
      const normalizedExistingColors = existingColorVariants.map((variant, idx) => ({
        ...variant,
        colorName: variant.colorName || variant.name || `Color ${idx + 1}`
      }));
      
      const updatedColorVariants = [...normalizedExistingColors, ...newColors];

      // Update product with new shade card and color variants
      const updatedProduct = await updateProduct(localFabric._id, {
        shadeCards: newShadeCards,
        colorVariants: updatedColorVariants,
        // For backward compatibility, also set the first shade card as legacy shadeCard
        shadeCard: newShadeCards[0] ? { url: newShadeCards[0].url } : null
      });

      // Update local fabric state
      setLocalFabric(updatedProduct);

      // Notify parent component if callback provided
      if (onFabricUpdated) {
        onFabricUpdated(updatedProduct);
      }

      if (showNotification) {
        const colorCount = newColors.length;
        showNotification(
          `Shade card uploaded successfully${colorCount > 0 ? ` and ${colorCount} color${colorCount > 1 ? 's' : ''} extracted` : ''}`,
          'success'
        );
      }
    } catch (err) {
      console.error('Failed to upload shade card:', err);
      if (showNotification) {
        showNotification(
          err?.response?.data?.message || err?.message || 'Failed to upload shade card',
          'error'
        );
      }
    } finally {
      setIsUploadingShadeCard(false);
      // Reset file input
      if (shadeCardInputRef.current) {
        shadeCardInputRef.current.value = '';
      }
    }
  };

  const handleShadeCardFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    await handleShadeCardUpload(file);
  };

  // Color editing handlers
  const handleStartEditColors = () => {
    // If no colors exist, start with empty array (user can add colors)
    // Otherwise, copy existing colors for editing
    const colorsToEdit = normalizedColorVariants.length > 0 
      ? normalizedColorVariants.map(v => ({ ...v }))
      : [];
    setEditingColorVariants(colorsToEdit);
    setIsEditingColors(true);
  };

  const handleCancelEditColors = () => {
    setEditingColorVariants([]);
    setIsEditingColors(false);
  };

  const handleColorNameChange = (index, newName) => {
    setEditingColorVariants(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], colorName: newName };
      return updated;
    });
  };

  const handleColorHexChange = (index, newHex) => {
    // Allow partial input while typing, but ensure it starts with #
    let cleanHex = newHex.trim();
    if (cleanHex && !cleanHex.startsWith('#')) {
      cleanHex = `#${cleanHex}`;
    }
    
    // Remove any invalid characters (only allow #, 0-9, A-F, a-f)
    cleanHex = cleanHex.replace(/[^#0-9A-Fa-f]/g, '');
    
    // Limit to 7 characters (# + 6 hex digits)
    if (cleanHex.length > 7) {
      cleanHex = cleanHex.substring(0, 7);
    }
    
    setEditingColorVariants(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], hex: cleanHex || '#' };
      return updated;
    });
  };

  const handleAddColor = () => {
    const newColor = {
      colorName: `Color ${editingColorVariants.length + 1}`,
      hex: '#000000'
    };
    setEditingColorVariants(prev => [...prev, newColor]);
  };

  const handleRemoveColor = (index) => {
    setEditingColorVariants(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveColors = async () => {
    if (!localFabric._id) return;

    if (editingColorVariants.length === 0) {
      if (showNotification) {
        showNotification('Please add at least one color', 'error');
      }
      return;
    }

    // Validate all colors have names and valid hex values
    const invalidColors = editingColorVariants.filter(v => {
      const hasName = v.colorName?.trim();
      const hex = v.hex?.trim() || '';
      const cleanHex = hex.startsWith('#') ? hex.substring(1) : hex;
      const isValidHex = /^[0-9A-Fa-f]{6}$/.test(cleanHex);
      return !hasName || !hex || !isValidHex;
    });

    if (invalidColors.length > 0) {
      if (showNotification) {
        showNotification('Please ensure all colors have valid names and 6-digit hex codes (e.g., #FF5733)', 'error');
      }
      return;
    }

    setIsSavingColors(true);
    try {
      // Normalize hex values (ensure they start with # and are uppercase)
      const normalizedColors = editingColorVariants.map(v => {
        let hex = v.hex.trim();
        if (!hex.startsWith('#')) {
          hex = `#${hex}`;
        }
        // Ensure hex is 6 digits (pad if needed, though validation should prevent this)
        const hexDigits = hex.substring(1);
        if (hexDigits.length === 6) {
          hex = `#${hexDigits.toUpperCase()}`;
        }
        
        return {
          colorName: v.colorName.trim(),
          hex: hex
        };
      });

      const updatedProduct = await updateProduct(localFabric._id, {
        colorVariants: normalizedColors
      });

      setLocalFabric(updatedProduct);
      
      if (onFabricUpdated) {
        onFabricUpdated(updatedProduct);
      }

      if (showNotification) {
        showNotification('Color variants updated successfully', 'success');
      }

      setIsEditingColors(false);
      setEditingColorVariants([]);
    } catch (err) {
      console.error('Failed to update color variants:', err);
      if (showNotification) {
        showNotification(
          err?.response?.data?.message || err?.message || 'Failed to update color variants',
          'error'
        );
      }
    } finally {
      setIsSavingColors(false);
    }
  };

  const handleStartEditProperties = () => {
    setEditingProperties(localFabric.properties ? [...localFabric.properties] : []);
    setIsEditingProperties(true);
  };

  const handleCancelEditProperties = () => {
    setEditingProperties(localFabric.properties || []);
    setIsEditingProperties(false);
  };

  const handlePropertyChange = (index, field, value) => {
    setEditingProperties(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddProperty = () => {
    setEditingProperties(prev => [...prev, { key: '', value: '' }]);
  };

  const handleRemoveProperty = (index) => {
    setEditingProperties(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleSaveProperties = async () => {
    if (!localFabric._id) return;

    try {
      setIsSavingProperties(true);
      
      // Filter out empty properties
      const validProperties = editingProperties
        .map(prop => ({
          key: (prop.key || '').trim(),
          value: (prop.value || '').trim()
        }))
        .filter(prop => prop.key && prop.value);

      const updatedProduct = await updateProduct(localFabric._id, {
        properties: validProperties
      });

      setLocalFabric(updatedProduct);
      setIsEditingProperties(false);

      if (onFabricUpdated) {
        onFabricUpdated(updatedProduct);
      }

      if (showNotification) {
        showNotification('Properties updated successfully', 'success');
      }
    } catch (error) {
      console.error('Failed to save properties:', error);
      if (showNotification) {
        showNotification(
          error?.response?.data?.message || error?.message || 'Failed to save properties',
          'error'
        );
      }
    } finally {
      setIsSavingProperties(false);
    }
  };

  const handleStartEditTags = () => {
    setEditingTags(Array.isArray(localFabric.tags) ? [...localFabric.tags] : []);
    setTagInput('');
    setIsEditingTags(true);
  };

  const handleCancelEditTags = () => {
    setEditingTags(Array.isArray(localFabric.tags) ? [...localFabric.tags] : []);
    setTagInput('');
    setIsEditingTags(false);
  };

  const handleTagAdd = () => {
    const value = tagInput.trim();
    if (!value) return;

    setEditingTags((prev) => {
      const normalized = value.toLowerCase();
      if (prev.length >= MAX_TAGS_PER_FABRIC) return prev;
      if (prev.some((tag) => String(tag).trim().toLowerCase() === normalized)) return prev;
      return [...prev, value];
    });
    setTagInput('');
  };

  const handleTagRemove = (tagToRemove) => {
    setEditingTags((prev) => prev.filter((tag) => tag !== tagToRemove));
  };

  const handleTagKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      handleTagAdd();
    } else if (event.key === 'Backspace' && !tagInput) {
      setEditingTags((prev) => prev.slice(0, -1));
    }
  };

  const handleSaveTags = async () => {
    if (!localFabric._id) return;

    try {
      setIsSavingTags(true);

      const sanitizedTags = editingTags
        .map((tag) => String(tag || '').trim())
        .filter(Boolean)
        .slice(0, MAX_TAGS_PER_FABRIC);

      const uniqueTags = [];
      const seen = new Set();
      for (const tag of sanitizedTags) {
        const key = tag.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        uniqueTags.push(tag);
      }

      const updatedProduct = await updateProduct(localFabric._id, {
        tags: uniqueTags
      });

      setLocalFabric(updatedProduct);
      setEditingTags(Array.isArray(updatedProduct.tags) ? [...updatedProduct.tags] : []);
      setIsEditingTags(false);
      setTagInput('');

      if (onFabricUpdated) {
        onFabricUpdated(updatedProduct);
      }

      if (showNotification) {
        showNotification('Tags updated successfully', 'success');
      }
    } catch (error) {
      console.error('Failed to save tags:', error);
      if (showNotification) {
        showNotification(
          error?.response?.data?.message || error?.message || 'Failed to save tags',
          'error'
        );
      }
    } finally {
      setIsSavingTags(false);
    }
  };

  // Fabric Analysis editing handlers
  const handleStartEditFabricAnalysis = () => {
    // Deep clone the fabric analysis to avoid mutating the original
    const fabricAnalysis = localFabric.fabricAnalysis || {};
    setEditingFabricAnalysis({
      fabric_analysis: {
        fabric_type: fabricAnalysis.fabric_analysis?.fabric_type || '',
        weave_or_knit: fabricAnalysis.fabric_analysis?.weave_or_knit || '',
        fiber_guess: fabricAnalysis.fabric_analysis?.fiber_guess || '',
        opacity: fabricAnalysis.fabric_analysis?.opacity || '',
        stretch: fabricAnalysis.fabric_analysis?.stretch || '',
        gsm_weight: fabricAnalysis.fabric_analysis?.gsm_weight || ''
      },
      visual_characteristics: {
        visual_type: fabricAnalysis.visual_characteristics?.visual_type || '',
        primary_colors: fabricAnalysis.visual_characteristics?.primary_colors || [],
        secondary_colors: fabricAnalysis.visual_characteristics?.secondary_colors || []
      },
      functional_properties: {
        season_suitability: fabricAnalysis.functional_properties?.season_suitability || [],
        climate_suitability: fabricAnalysis.functional_properties?.climate_suitability || []
      },
      women_garment_recommendations: {
        best_suited: fabricAnalysis.women_garment_recommendations?.best_suited || []
      },
      men_garment_recommendations: {
        best_suited: fabricAnalysis.men_garment_recommendations?.best_suited || []
      },
      design_guidelines: {
        ideal_garment_fit: fabricAnalysis.design_guidelines?.ideal_garment_fit || '',
        suggested_necklines: fabricAnalysis.design_guidelines?.suggested_necklines || [],
        suggested_sleeves: fabricAnalysis.design_guidelines?.suggested_sleeves || [],
        ideal_length_options: fabricAnalysis.design_guidelines?.ideal_length_options || [],
        print_or_cutting_tips: fabricAnalysis.design_guidelines?.print_or_cutting_tips || []
      }
    });
    setIsEditingFabricAnalysis(true);
  };

  const handleCancelEditFabricAnalysis = () => {
    setEditingFabricAnalysis(null);
    setIsEditingFabricAnalysis(false);
  };

  const findTextureByFabricType = useCallback((fabricType) => {
    if (!fabricType) return null;
    for (const cat of FABRIC_TEXTURES.fabricTextures || []) {
      const t = cat.types?.find((x) => x.name === fabricType);
      if (t) return { ...t, categoryName: cat.category };
    }
    return { name: fabricType, categoryName: 'Other', description: '' };
  }, []);

  const mergeFabricAnalysisSelectOptions = (currentValue, options) => [
    ...new Set([
      ...(currentValue && !options.includes(currentValue) ? [currentValue] : []),
      ...options
    ])
  ];

  const handleFabricAnalysisFieldChange = (section, field, value) => {
    setEditingFabricAnalysis(prev => {
      const updated = { ...prev };
      if (!updated[section]) {
        updated[section] = {};
      }
      updated[section] = { ...updated[section], [field]: value };
      return updated;
    });
  };

  const handleArrayFieldChange = (section, field, value) => {
    // Convert comma-separated string to array
    const arrayValue = value ? value.split(',').map(item => item.trim()).filter(item => item) : [];
    handleFabricAnalysisFieldChange(section, field, arrayValue);
  };

  const handleGarmentRecommendationChange = (gender, index, field, value) => {
    setEditingFabricAnalysis(prev => {
      const updated = { ...prev };
      const key = gender === 'women' ? 'women_garment_recommendations' : 'men_garment_recommendations';
      if (!updated[key]) {
        updated[key] = { best_suited: [] };
      }
      const recommendations = [...(updated[key].best_suited || [])];
      if (!recommendations[index]) {
        recommendations[index] = { garment: '', reason: '', silhouette: '', design_tips: [] };
      }
      recommendations[index] = { ...recommendations[index], [field]: value };
      updated[key] = { best_suited: recommendations };
      return updated;
    });
  };

  const handleAddGarmentRecommendation = (gender) => {
    setEditingFabricAnalysis(prev => {
      const updated = { ...prev };
      const key = gender === 'women' ? 'women_garment_recommendations' : 'men_garment_recommendations';
      if (!updated[key]) {
        updated[key] = { best_suited: [] };
      }
      updated[key] = {
        best_suited: [
          ...(updated[key].best_suited || []),
          { garment: '', reason: '', silhouette: '', design_tips: [] }
        ]
      };
      return updated;
    });
  };

  const handleRemoveGarmentRecommendation = (gender, index) => {
    setEditingFabricAnalysis(prev => {
      const updated = { ...prev };
      const key = gender === 'women' ? 'women_garment_recommendations' : 'men_garment_recommendations';
      if (updated[key]?.best_suited) {
        updated[key] = {
          best_suited: updated[key].best_suited.filter((_, i) => i !== index)
        };
      }
      return updated;
    });
  };

  const handleSaveFabricAnalysis = async () => {
    if (!localFabric._id) return;

    try {
      setIsSavingFabricAnalysis(true);

      // Clean up empty fields
      const cleanedAnalysis = { ...editingFabricAnalysis };

      // Clean fabric_analysis
      if (cleanedAnalysis.fabric_analysis) {
        Object.keys(cleanedAnalysis.fabric_analysis).forEach(key => {
          if (!cleanedAnalysis.fabric_analysis[key]) {
            delete cleanedAnalysis.fabric_analysis[key];
          }
        });
        if (Object.keys(cleanedAnalysis.fabric_analysis).length === 0) {
          delete cleanedAnalysis.fabric_analysis;
        }
      }

      // Clean arrays - remove empty arrays
      if (cleanedAnalysis.visual_characteristics) {
        if (!cleanedAnalysis.visual_characteristics.visual_type) {
          delete cleanedAnalysis.visual_characteristics.visual_type;
        }
        if (cleanedAnalysis.visual_characteristics.primary_colors?.length === 0) {
          delete cleanedAnalysis.visual_characteristics.primary_colors;
        }
        if (cleanedAnalysis.visual_characteristics.secondary_colors?.length === 0) {
          delete cleanedAnalysis.visual_characteristics.secondary_colors;
        }
        if (Object.keys(cleanedAnalysis.visual_characteristics).length === 0) {
          delete cleanedAnalysis.visual_characteristics;
        }
      }

      if (cleanedAnalysis.functional_properties) {
        if (cleanedAnalysis.functional_properties.season_suitability?.length === 0) {
          delete cleanedAnalysis.functional_properties.season_suitability;
        }
        if (cleanedAnalysis.functional_properties.climate_suitability?.length === 0) {
          delete cleanedAnalysis.functional_properties.climate_suitability;
        }
        if (Object.keys(cleanedAnalysis.functional_properties).length === 0) {
          delete cleanedAnalysis.functional_properties;
        }
      }

      // Clean garment recommendations
      if (cleanedAnalysis.women_garment_recommendations?.best_suited?.length === 0) {
        delete cleanedAnalysis.women_garment_recommendations;
      }
      if (cleanedAnalysis.men_garment_recommendations?.best_suited?.length === 0) {
        delete cleanedAnalysis.men_garment_recommendations;
      }

      // Clean design guidelines
      if (cleanedAnalysis.design_guidelines) {
        if (!cleanedAnalysis.design_guidelines.ideal_garment_fit) {
          delete cleanedAnalysis.design_guidelines.ideal_garment_fit;
        }
        if (cleanedAnalysis.design_guidelines.suggested_necklines?.length === 0) {
          delete cleanedAnalysis.design_guidelines.suggested_necklines;
        }
        if (cleanedAnalysis.design_guidelines.suggested_sleeves?.length === 0) {
          delete cleanedAnalysis.design_guidelines.suggested_sleeves;
        }
        if (cleanedAnalysis.design_guidelines.ideal_length_options?.length === 0) {
          delete cleanedAnalysis.design_guidelines.ideal_length_options;
        }
        if (cleanedAnalysis.design_guidelines.print_or_cutting_tips?.length === 0) {
          delete cleanedAnalysis.design_guidelines.print_or_cutting_tips;
        }
        if (Object.keys(cleanedAnalysis.design_guidelines).length === 0) {
          delete cleanedAnalysis.design_guidelines;
        }
      }

      const updatedProduct = await updateProduct(localFabric._id, {
        fabricAnalysis: cleanedAnalysis
      });

      setLocalFabric(updatedProduct);
      setIsEditingFabricAnalysis(false);
      setEditingFabricAnalysis(null);

      if (onFabricUpdated) {
        onFabricUpdated(updatedProduct);
      }

      if (showNotification) {
        showNotification('Fabric analysis updated successfully', 'success');
      }
    } catch (error) {
      console.error('Failed to save fabric analysis:', error);
      if (showNotification) {
        showNotification(
          error?.response?.data?.message || error?.message || 'Failed to save fabric analysis',
          'error'
        );
      }
    } finally {
      setIsSavingFabricAnalysis(false);
    }
  };

  // Handle generate garment design from this fabric
  const handleGenerateGarmentDesign = useCallback(() => {
    try {
      const token = localStorage.getItem('token');
      
      // Prepare all variants for auto-selection
      const allVariants = [];
      
      // Add primary image as first variant
      const primaryImageUrl = localFabric.images?.original?.s3Url || 
                              localFabric.images?.original?.imgbbUrl || 
                              localFabric.images?.original?.url || 
                              localFabric.image;
      
      if (primaryImageUrl) {
        allVariants.push({
          image: { s3Url: primaryImageUrl },
          colorName: 'Primary',
          isPrimary: true
        });
      }
      
      // Add all color variants
      if (localFabric.colorVariants && localFabric.colorVariants.length > 0) {
        localFabric.colorVariants.forEach((variant, index) => {
          allVariants.push({
            ...variant,
            isPrimary: false,
            originalIndex: index
          });
        });
      }
      
      // Auto-populate product details with variant information
      const productDetails = {
        name: localFabric.name || '',
        description: localFabric.description || '',
        image: primaryImageUrl || '',
        category: localFabric.category || '',
        type: localFabric.productType || localFabric.type || '',
        targetAudience: localFabric.targetAudience || 'General Audience',
        fabricAnalysis: localFabric.fabricAnalysis || null,
        fabricWeight: localFabric.fabricAnalysis?.fabric_analysis?.gsm_weight || localFabric.fabricWeight || '',
        selectedProduct: localFabric,
        colorVariants: localFabric.colorVariants || [],
        selectedVariants: allVariants,
        selectedVariantIndex: 0
      };
      
      if (!token) {
        // User not logged in - store fabric and show login modal
        localStorage.setItem('selectedFabricForGeneration', JSON.stringify(productDetails));
        if (onOpenLoginModal && typeof onOpenLoginModal === 'function') {
          onOpenLoginModal();
        } else if (setActiveView) {
          setActiveView('login');
        }
        if (showNotification) {
          showNotification('Please login to generate garment designs with this fabric', 'info');
        }
        return;
      }
      
      // User is logged in - set product details and navigate to generate page
      if (setProductDetails) {
        setProductDetails(productDetails);
      }
      
      if (setPreSelectedProduct) {
        setPreSelectedProduct(localFabric);
      }
      
      // Close the fabric detail view first (before navigation)
      if (onClose) {
        onClose();
      }
      
      // Show success notification
      const variantCount = allVariants.length;
      if (showNotification) {
        showNotification(
          `Fabric "${localFabric.name}" loaded${variantCount > 1 ? ` with ${variantCount} color variant${variantCount > 1 ? 's' : ''}` : ''}. Ready to generate!`,
          'success'
        );
      }
      
      // Navigate to generate kit page (after closing)
      if (setActiveView) {
        // Use setTimeout to ensure the close animation completes first
        setTimeout(() => {
          setActiveView('generate');
        }, 100);
      }
    } catch (error) {
      console.error('Error generating garment design:', error);
      if (showNotification) {
        showNotification('Failed to load fabric for generation', 'error');
      }
    }
  }, [localFabric, setProductDetails, setPreSelectedProduct, setActiveView, onOpenLoginModal, showNotification, onClose]);

  const canGenerateKit = !!onGenerateFabric || !!setProductDetails || !!setActiveView;

  const handleGenerateKit = useCallback(() => {
    if (onGenerateFabric) {
      onGenerateFabric(localFabric);
      return;
    }
    handleGenerateGarmentDesign();
  }, [onGenerateFabric, localFabric, handleGenerateGarmentDesign]);

  const canOneClickMultiConcept = useMemo(() => {
    if (!isAdmin) return false;
    if (!fabricImage) return false;
    if (!localFabric?._id && !hasGarmentTypeInsights(localFabric?.fabricAnalysis)) return false;
    // Prefer having insights locally; sourceProductId alone may still work on the server.
    return true;
  }, [isAdmin, fabricImage, localFabric?._id, localFabric?.fabricAnalysis]);

  const buildMultiConceptPayload = useCallback(() => {
    const insights = getGarmentTypeInsights(localFabric?.fabricAnalysis);
    const concepts = GARMENT_INSIGHT_AUDIENCES.map((audience) => {
      const top = (insights[audience.key] || [])[0];
      if (!top?.type_label) return null;
      return {
        audience: audience.key,
        category: audience.category,
        type_value: top.type_value,
        type_label: top.type_label,
        reason: top.reason || '',
      };
    }).filter(Boolean);

    const fa = localFabric?.fabricAnalysis?.fabric_analysis || {};
    const advancedSettings = {
      ...(localFabric?.fabricTexture
        ? {
            fabricTexture: localFabric.fabricTexture.name || localFabric.fabricTexture,
            fabricTextureCategory: localFabric.fabricTexture.category,
            fabricTextureDescription: localFabric.fabricTexture.description,
          }
        : {}),
      ...(fa.opacity ? { opacity: fa.opacity } : {}),
      ...(fa.stretch ? { stretch: fa.stretch } : {}),
      ...(fa.gsm_weight || localFabric?.fabricWeight
        ? { fabricWeight: fa.gsm_weight || localFabric.fabricWeight }
        : {}),
      environmentPreset: 'studio',
    };

    return {
      productImageUrl: fabricImage,
      concepts: concepts.length === 4 ? concepts : null,
      fabricAnalysis: localFabric?.fabricAnalysis || null,
      sourceProductId: localFabric?._id || localFabric?.id || null,
      saveToKit: true,
      kitName: `${localFabric?.name || 'Fabric'} · Multi-Concept Capsule`,
      productName: localFabric?.name || 'Multi-Concept Capsule',
      productDescription: `One-click multi-audience concepts from ${localFabric?.name || 'this fabric'}`,
      brandProfile: {
        brandName: 'Fashdeck',
        logo: 'https://fashdeck.com/logo.png',
      },
      advancedSettings,
    };
  }, [fabricImage, localFabric]);

  const refreshFabricKits = useCallback(async () => {
    if (!fabricImage) return;
    try {
      const token = localStorage.getItem('token');
      const response = token
        ? await getKitsByFabricImage(fabricImage)
        : await getKitsByFabricImagePublic(fabricImage);
      const fabricKits = response.data?.data?.kits || response.data?.kits || [];
      setKits(fabricKits);
    } catch (error) {
      console.error('Error refreshing kits after multi-concept:', error);
    }
  }, [fabricImage]);

  const handleOneClickMultiConcept = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      if (onOpenLoginModal) onOpenLoginModal();
      else if (setActiveView) setActiveView('login');
      showNotification?.('Please login to generate a multi-concept kit', 'info');
      return;
    }

    if (!fabricImage) {
      showNotification?.('Fabric image is required for generation', 'error');
      return;
    }

    if (!hasGarmentTypeInsights(localFabric?.fabricAnalysis) && !localFabric?._id) {
      showNotification?.(
        'Run fabric analysis first so we can pick top garments for women, men, and kids.',
        'warning'
      );
      return;
    }

    setIsGeneratingMultiConcept(true);
    setMultiConceptResult(null);
    try {
      const payload = buildMultiConceptPayload();
      const result = await generateMultiConceptImage(payload);
      const imageUrl = result?.data?.finalImageUrl;
      const kitId = result?.data?.savedKit?.id;

      if (!result?.success || !imageUrl) {
        throw new Error(result?.message || 'Multi-concept generation failed');
      }

      setMultiConceptResult({
        imageUrl,
        kitId,
        concepts: result?.data?.concepts || payload.concepts || [],
        kitName: result?.data?.savedKit?.name,
      });

      showNotification?.(
        result?.data?.savedKit
          ? `Multi-concept kit "${result.data.savedKit.name}" created`
          : 'Multi-concept image generated',
        'success'
      );

      await refreshFabricKits();

      // Scroll kits section into view when present
      requestAnimationFrame(() => {
        contentScrollRef.current
          ?.querySelector('[data-fabric-kits-section]')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } catch (error) {
      console.error('One-click multi-concept failed:', error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to generate multi-concept kit';
      showNotification?.(message, 'error');
    } finally {
      setIsGeneratingMultiConcept(false);
    }
  }, [
    fabricImage,
    localFabric,
    buildMultiConceptPayload,
    refreshFabricKits,
    onOpenLoginModal,
    setActiveView,
    showNotification,
  ]);

  // Handle opening color variants modal
  const handleViewColorVariants = useCallback((kit) => {
    setSelectedKitForColorVariants(kit);
    setColorVariantsModalOpen(true);
  }, []);

  // Handle closing color variants modal
  const handleCloseColorVariantsModal = useCallback(() => {
    setColorVariantsModalOpen(false);
    setSelectedKitForColorVariants(null);
  }, []);

  // Refresh kits after color variant generation
  const handleRefreshKits = useCallback(async () => {
    if (!fabricImage) return;

    try {
      setIsLoadingKits(true);
      const token = localStorage.getItem('token');
      const fabricKits = token 
        ? await getKitsByFabricImage(fabricImage)
        : await getKitsByFabricImagePublic(fabricImage);
      
      setKits(fabricKits);
      
      // Update selected kit if modal is open
      if (selectedKitForColorVariants) {
        const updatedKit = fabricKits.find(k => k._id === selectedKitForColorVariants._id);
        if (updatedKit) {
          setSelectedKitForColorVariants(updatedKit);
        }
      }
    } catch (error) {
      console.error('Error refreshing kits:', error);
    } finally {
      setIsLoadingKits(false);
    }
  }, [fabricImage, selectedKitForColorVariants]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-hidden">
      <div className="h-full w-full flex flex-col bg-white">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 sm:px-6 pb-4 pt-[max(1rem,env(safe-area-inset-top))] flex items-center justify-between shadow-lg">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <button
              onClick={onClose}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-white/20 transition-colors active:scale-95"
              title="Go back"
              aria-label="Go back"
            >
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">{localFabric.name}</h1>
              <p className="text-sm text-purple-100">{liteMode ? 'Fabric Detail' : 'Fabric Catalogue'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canGenerateKit && (
              <button
                onClick={handleGenerateKit}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-yellow-500 hover:bg-yellow-600 rounded-lg transition-colors shadow-lg"
                title="Generate kit from this fabric"
              >
                <Sparkles size={18} />
                <span className="hidden sm:inline text-sm font-medium">Generate Kit</span>
              </button>
            )}
            {canOneClickMultiConcept && (
              <button
                type="button"
                onClick={handleOneClickMultiConcept}
                disabled={isGeneratingMultiConcept}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors shadow-lg disabled:opacity-80 ${
                  isGeneratingMultiConcept
                    ? 'bg-fuchsia-400 cursor-wait'
                    : 'bg-fuchsia-500 hover:bg-fuchsia-600'
                }`}
                title="One-click multi-concept kit (Women, Men, Kids Boy, Kids Girl)"
              >
                {isGeneratingMultiConcept ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Wand2 size={18} />
                )}
                <span className="hidden sm:inline text-sm font-medium">
                  {isGeneratingMultiConcept ? 'Generating…' : 'Multi Concept'}
                </span>
              </button>
            )}
            {!liteMode && (
            <button
              onClick={handleWhatsAppShare}
              disabled={isSharingWhatsApp || isLoadingKits || kits.length === 0}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors shadow-lg ${
                isSharingWhatsApp || isLoadingKits || kits.length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-500 hover:bg-green-600'
              }`}
              title={kits.length === 0 ? 'No kits available to share' : 'Share catalogue on WhatsApp'}
            >
              {isSharingWhatsApp ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  <span className="hidden sm:inline text-sm font-medium">Sharing...</span>
                </>
              ) : (
                <>
                  <MessageCircle size={18} />
                  <span className="hidden sm:inline text-sm font-medium">Share on WhatsApp</span>
                </>
              )}
            </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              title="Close"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div ref={contentScrollRef} className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
            
            {/* Main Fabric Section */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
              <div className={`grid grid-cols-1 ${liteMode ? '' : 'md:grid-cols-2'} gap-6 p-6`}>
                {/* Fabric Image + Name */}
                <div className="space-y-3">
                  <div className={`relative bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl overflow-hidden ${liteMode ? 'aspect-[4/3] sm:aspect-square max-h-[320px] mx-auto w-full' : 'aspect-square'}`}>
                    {!isImageBroken(fabricImage) ? (
                      <img
                        {...imageInteractionProps}
                        src={fabricImage}
                        alt={localFabric.name}
                        className="w-full h-full object-contain cursor-pointer md:hover:scale-105 transition-transform duration-300"
                        onClick={() => handleImageClick(fabricImage)}
                        onError={() => handleImageError(fabricImage)}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                        <div className="text-center">
                          <ImageIcon size={48} className="mx-auto text-gray-300 mb-2" />
                          <p className="text-sm text-gray-500">Image unavailable</p>
                        </div>
                      </div>
                    )}
                    {variantCount > 0 && (
                      <div className="absolute top-4 right-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                        +{variantCount} variants
                      </div>
                    )}
                    {localFabric.fabricTexture && (
                      <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                        {localFabric.fabricTexture.name}
                      </div>
                    )}
                  </div>

                  {!liteMode && (
                    <div className="px-1">
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 leading-tight">{localFabric.name}</h2>
                    </div>
                  )}
                </div>

                {/* Fabric Details */}
                <div className="space-y-4">
                  {/* Description removed (keep details compact) */}

                  {canGenerateKit && (
                    <button
                      onClick={handleGenerateKit}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold transition-colors shadow-lg"
                      title="Generate kit from this fabric"
                    >
                      <Sparkles size={18} />
                      <span>Generate Kit</span>
                    </button>
                  )}

                  {canOneClickMultiConcept && (
                    <button
                      type="button"
                      onClick={handleOneClickMultiConcept}
                      disabled={isGeneratingMultiConcept}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-white rounded-lg font-semibold transition-colors shadow-lg disabled:opacity-80 ${
                        isGeneratingMultiConcept
                          ? 'bg-fuchsia-400 cursor-wait'
                          : 'bg-fuchsia-500 hover:bg-fuchsia-600'
                      }`}
                      title="Generate one lookbook kit with Women, Men, Kids Boy & Kids Girl from fabric recommendations"
                    >
                      {isGeneratingMultiConcept ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          <span>Generating multi-concept kit…</span>
                        </>
                      ) : (
                        <>
                          <Wand2 size={18} />
                          <span>One-Click Multi Concept</span>
                        </>
                      )}
                    </button>
                  )}

                  {multiConceptResult?.imageUrl && (
                    <div className="rounded-xl border border-fuchsia-200 bg-fuchsia-50/70 p-3 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-fuchsia-900">Multi-concept kit ready</p>
                          <p className="text-xs text-fuchsia-700 mt-0.5 truncate">
                            {multiConceptResult.kitName || 'Saved to your creations'}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setMultiConceptResult(null)}
                          className="p-1 rounded-md text-fuchsia-700 hover:bg-fuchsia-100"
                          aria-label="Dismiss"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <button
                        type="button"
                        className="block w-full overflow-hidden rounded-lg border border-fuchsia-100 bg-white"
                        onClick={() => handleImageClick(multiConceptResult.imageUrl)}
                      >
                        <img
                          src={multiConceptResult.imageUrl}
                          alt="Multi-concept generation"
                          className="w-full max-h-64 object-contain"
                        />
                      </button>
                      {Array.isArray(multiConceptResult.concepts) && multiConceptResult.concepts.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {multiConceptResult.concepts.map((c) => (
                            <span
                              key={`${c.audience}-${c.type_label}`}
                              className="inline-flex rounded-full bg-white border border-fuchsia-100 px-2 py-0.5 text-[10px] font-semibold text-fuchsia-800"
                            >
                              {c.label || c.audience}: {c.type_label}
                            </span>
                          ))}
                        </div>
                      )}
                      {multiConceptResult.kitId && openAiStudio && (
                        <button
                          type="button"
                          onClick={() => {
                            const kit =
                              kits.find((k) => String(k._id) === String(multiConceptResult.kitId)) || {
                                _id: multiConceptResult.kitId,
                                generatedImageUrl: multiConceptResult.imageUrl,
                                name: multiConceptResult.kitName,
                              };
                            openAiStudio(kit, { returnTo: { type: 'fabric', fabric: localFabric } });
                          }}
                          className="w-full rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-sm font-semibold py-2"
                        >
                          Open in Studio
                        </button>
                      )}
                    </div>
                  )}

                  {localFabric.fabricTexture && (
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4">
                      <p className="text-sm text-purple-600 font-medium mb-2">Fabric Texture</p>
                      <div className="space-y-1">
                        <p className="font-semibold text-gray-800">{localFabric.fabricTexture.name}</p>
                        {localFabric.fabricTexture.description && (
                          <p className="text-sm text-gray-600">{localFabric.fabricTexture.description}</p>
                        )}
                        {localFabric.fabricTexture.categoryName && (
                          <p className="text-xs text-gray-500">Category: {localFabric.fabricTexture.categoryName}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Fabric Analysis Section */}
                  {(localFabric.fabricAnalysis || isEditingFabricAnalysis) && (
                    <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-xl border border-indigo-200 p-3 sm:p-4 space-y-3">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Sparkles size={16} className="text-indigo-600" />
                          <p className="text-sm font-semibold text-indigo-700">Fabric Analysis</p>
                        </div>
                        {canEditFabricAnalysis && !isEditingFabricAnalysis && (
                          <button
                            onClick={handleStartEditFabricAnalysis}
                            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                          >
                            <Edit2 size={14} />
                            Edit
                          </button>
                        )}
                        {isEditingFabricAnalysis && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={handleCancelEditFabricAnalysis}
                              disabled={isSavingFabricAnalysis}
                              className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleSaveFabricAnalysis}
                              disabled={isSavingFabricAnalysis}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                              {isSavingFabricAnalysis ? (
                                <>
                                  <Loader2 size={14} className="animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Save size={14} />
                                  Save
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Fabric Analysis Details */}
                      {isEditingFabricAnalysis ? (
                        <div className="bg-white/60 rounded-lg p-3 space-y-3">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Fabric Properties</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-gray-600 mb-1 block">Fabric Type</label>
                              <TextureSelector
                                selectedTexture={findTextureByFabricType(editingFabricAnalysis.fabric_analysis?.fabric_type)}
                                onTextureChange={(texture) =>
                                  handleFabricAnalysisFieldChange('fabric_analysis', 'fabric_type', texture ? texture.name : '')
                                }
                                showTip={false}
                                onTipToggle={() => {}}
                                dropdownMode="inline"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-600 mb-1 block">Weave/Knit</label>
                              <div className="relative">
                                <select
                                  value={editingFabricAnalysis.fabric_analysis?.weave_or_knit || ''}
                                  onChange={(e) => handleFabricAnalysisFieldChange('fabric_analysis', 'weave_or_knit', e.target.value)}
                                  className="w-full appearance-none rounded border border-gray-300 bg-white px-2 py-1.5 pr-8 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="">Select weave/knit</option>
                                  {mergeFabricAnalysisSelectOptions(
                                    editingFabricAnalysis.fabric_analysis?.weave_or_knit,
                                    WEAVE_OR_KNIT_OPTIONS
                                  ).map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                                <ChevronDown size={14} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
                              </div>
                            </div>
                            <div>
                              <label className="text-xs text-gray-600 mb-1 block">Fiber</label>
                              <div className="relative">
                                <select
                                  value={editingFabricAnalysis.fabric_analysis?.fiber_guess || ''}
                                  onChange={(e) => handleFabricAnalysisFieldChange('fabric_analysis', 'fiber_guess', e.target.value)}
                                  className="w-full appearance-none rounded border border-gray-300 bg-white px-2 py-1.5 pr-8 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="">Select fiber</option>
                                  {mergeFabricAnalysisSelectOptions(
                                    editingFabricAnalysis.fabric_analysis?.fiber_guess,
                                    FIBER_OPTIONS
                                  ).map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                                <ChevronDown size={14} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
                              </div>
                            </div>
                            <div>
                              <label className="text-xs text-gray-600 mb-1 block">Opacity</label>
                              <div className="relative">
                                <select
                                  value={editingFabricAnalysis.fabric_analysis?.opacity || ''}
                                  onChange={(e) => handleFabricAnalysisFieldChange('fabric_analysis', 'opacity', e.target.value)}
                                  className="w-full appearance-none rounded border border-gray-300 bg-white px-2 py-1.5 pr-8 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="">Select opacity</option>
                                  {mergeFabricAnalysisSelectOptions(
                                    editingFabricAnalysis.fabric_analysis?.opacity,
                                    OPACITY_OPTIONS
                                  ).map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                                <ChevronDown size={14} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
                              </div>
                            </div>
                            <div>
                              <label className="text-xs text-gray-600 mb-1 block">Stretch</label>
                              <div className="relative">
                                <select
                                  value={editingFabricAnalysis.fabric_analysis?.stretch || ''}
                                  onChange={(e) => handleFabricAnalysisFieldChange('fabric_analysis', 'stretch', e.target.value)}
                                  className="w-full appearance-none rounded border border-gray-300 bg-white px-2 py-1.5 pr-8 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="">Select stretch</option>
                                  {mergeFabricAnalysisSelectOptions(
                                    editingFabricAnalysis.fabric_analysis?.stretch,
                                    STRETCH_OPTIONS
                                  ).map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                                <ChevronDown size={14} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
                              </div>
                            </div>
                            <div className="sm:col-span-2">
                              <label className="text-xs text-gray-600 mb-1 block">GSM Weight</label>
                              <div className="relative">
                                <select
                                  value={editingFabricAnalysis.fabric_analysis?.gsm_weight || ''}
                                  onChange={(e) => handleFabricAnalysisFieldChange('fabric_analysis', 'gsm_weight', e.target.value)}
                                  className="w-full appearance-none rounded border border-gray-300 bg-white px-2 py-1.5 pr-8 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="">Select GSM weight</option>
                                  {mergeFabricAnalysisSelectOptions(
                                    editingFabricAnalysis.fabric_analysis?.gsm_weight,
                                    GSM_WEIGHT_OPTIONS
                                  ).map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                                <ChevronDown size={14} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : localFabric.fabricAnalysis?.fabric_analysis && (
                        <div className="bg-white/60 rounded-lg p-3 space-y-2">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Fabric Properties</p>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            {localFabric.fabricAnalysis.fabric_analysis.fabric_type && (
                              <div>
                                <span className="text-gray-600">Type:</span>
                                <span className="ml-1 font-medium text-gray-800">{localFabric.fabricAnalysis.fabric_analysis.fabric_type}</span>
                              </div>
                            )}
                            {localFabric.fabricAnalysis.fabric_analysis.weave_or_knit && (
                              <div>
                                <span className="text-gray-600">Weave/Knit:</span>
                                <span className="ml-1 font-medium text-gray-800">{localFabric.fabricAnalysis.fabric_analysis.weave_or_knit}</span>
                              </div>
                            )}
                            {localFabric.fabricAnalysis.fabric_analysis.fiber_guess && (
                              <div>
                                <span className="text-gray-600">Fiber:</span>
                                <span className="ml-1 font-medium text-gray-800">{localFabric.fabricAnalysis.fabric_analysis.fiber_guess}</span>
                              </div>
                            )}
                            {localFabric.fabricAnalysis.fabric_analysis.opacity && (
                              <div>
                                <span className="text-gray-600">Opacity:</span>
                                <span className="ml-1 font-medium text-gray-800">{localFabric.fabricAnalysis.fabric_analysis.opacity}</span>
                              </div>
                            )}
                            {localFabric.fabricAnalysis.fabric_analysis.stretch && (
                              <div>
                                <span className="text-gray-600">Stretch:</span>
                                <span className="ml-1 font-medium text-gray-800">{localFabric.fabricAnalysis.fabric_analysis.stretch}</span>
                              </div>
                            )}
                            {localFabric.fabricAnalysis.fabric_analysis.gsm_weight && (
                              <div>
                                <span className="text-gray-600">GSM Weight:</span>
                                <span className="ml-1 font-medium text-gray-800">{localFabric.fabricAnalysis.fabric_analysis.gsm_weight}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Visual Characteristics */}
                      {isEditingFabricAnalysis ? (
                        <div className="bg-white/60 rounded-lg p-3 space-y-3">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Visual Characteristics</p>
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">Fabric Visual</label>
                            <div className="relative">
                              <select
                                value={editingFabricAnalysis.visual_characteristics?.visual_type || ''}
                                onChange={(e) => handleFabricAnalysisFieldChange('visual_characteristics', 'visual_type', e.target.value)}
                                className="w-full appearance-none rounded border border-gray-300 bg-white px-2 py-1.5 pr-8 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Select fabric visual</option>
                                {mergeFabricAnalysisSelectOptions(
                                  editingFabricAnalysis.visual_characteristics?.visual_type,
                                  FABRIC_VISUAL_TYPE_OPTIONS
                                ).map((opt) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                              <ChevronDown size={14} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">Primary Colors (comma-separated)</label>
                            <input
                              type="text"
                              value={(editingFabricAnalysis.visual_characteristics?.primary_colors || []).join(', ')}
                              onChange={(e) => handleArrayFieldChange('visual_characteristics', 'primary_colors', e.target.value)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., Red, Blue, Green"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">Secondary Colors (comma-separated)</label>
                            <input
                              type="text"
                              value={(editingFabricAnalysis.visual_characteristics?.secondary_colors || []).join(', ')}
                              onChange={(e) => handleArrayFieldChange('visual_characteristics', 'secondary_colors', e.target.value)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., Yellow, Orange"
                            />
                          </div>
                        </div>
                      ) : localFabric.fabricAnalysis?.visual_characteristics && (
                        <div className="bg-white/60 rounded-lg p-3 space-y-2">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Visual Characteristics</p>
                          {localFabric.fabricAnalysis.visual_characteristics.visual_type && (
                            <div>
                              <span className="text-xs text-gray-600">Fabric Visual: </span>
                              <span className="text-xs font-medium text-gray-800">
                                {localFabric.fabricAnalysis.visual_characteristics.visual_type}
                              </span>
                            </div>
                          )}
                          {localFabric.fabricAnalysis.visual_characteristics.primary_colors && 
                           localFabric.fabricAnalysis.visual_characteristics.primary_colors.length > 0 && (
                            <div>
                              <span className="text-xs text-gray-600">Primary Colors: </span>
                              <span className="text-xs font-medium text-gray-800">
                                {localFabric.fabricAnalysis.visual_characteristics.primary_colors.join(', ')}
                              </span>
                            </div>
                          )}
                          {localFabric.fabricAnalysis.visual_characteristics.secondary_colors && 
                           localFabric.fabricAnalysis.visual_characteristics.secondary_colors.length > 0 && (
                            <div>
                              <span className="text-xs text-gray-600">Secondary Colors: </span>
                              <span className="text-xs font-medium text-gray-800">
                                {localFabric.fabricAnalysis.visual_characteristics.secondary_colors.join(', ')}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Functional Properties */}
                      {isEditingFabricAnalysis ? (
                        <div className="bg-white/60 rounded-lg p-3 space-y-3">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Functional Properties</p>
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">Season Suitability (comma-separated)</label>
                            <input
                              type="text"
                              value={(editingFabricAnalysis.functional_properties?.season_suitability || []).join(', ')}
                              onChange={(e) => handleArrayFieldChange('functional_properties', 'season_suitability', e.target.value)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., Summer, Winter, All Season"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">Climate Suitability (comma-separated)</label>
                            <input
                              type="text"
                              value={(editingFabricAnalysis.functional_properties?.climate_suitability || []).join(', ')}
                              onChange={(e) => handleArrayFieldChange('functional_properties', 'climate_suitability', e.target.value)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., Hot, Cold, Humid"
                            />
                          </div>
                        </div>
                      ) : localFabric.fabricAnalysis?.functional_properties && (
                        <div className="bg-white/60 rounded-lg p-3 space-y-2">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Functional Properties</p>
                          {localFabric.fabricAnalysis.functional_properties.season_suitability && 
                           localFabric.fabricAnalysis.functional_properties.season_suitability.length > 0 && (
                            <div>
                              <span className="text-xs text-gray-600">Season: </span>
                              <span className="text-xs font-medium text-gray-800">
                                {localFabric.fabricAnalysis.functional_properties.season_suitability.join(', ')}
                              </span>
                            </div>
                          )}
                          {localFabric.fabricAnalysis.functional_properties.climate_suitability && 
                           localFabric.fabricAnalysis.functional_properties.climate_suitability.length > 0 && (
                            <div>
                              <span className="text-xs text-gray-600">Climate: </span>
                              <span className="text-xs font-medium text-gray-800">
                                {localFabric.fabricAnalysis.functional_properties.climate_suitability.join(', ')}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Garment Recommendations */}
                      {isEditingFabricAnalysis ? (
                        <div className="bg-white/60 rounded-lg p-3 space-y-4">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Garment Recommendations</p>
                          
                          {/* Women's Garments */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-medium text-pink-600">Women's Garments</p>
                              <button
                                onClick={() => handleAddGarmentRecommendation('women')}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-pink-500 text-white rounded hover:bg-pink-600"
                              >
                                <Plus size={12} />
                                Add
                              </button>
                            </div>
                            <div className="space-y-2">
                              {(editingFabricAnalysis.women_garment_recommendations?.best_suited || []).map((rec, idx) => (
                                <div key={idx} className="bg-pink-50 rounded p-2 space-y-2 border border-pink-200">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-pink-700">Recommendation #{idx + 1}</span>
                                    <button
                                      onClick={() => handleRemoveGarmentRecommendation('women', idx)}
                                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                                      title="Remove"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </div>
                                  <input
                                    type="text"
                                    value={rec.garment || ''}
                                    onChange={(e) => handleGarmentRecommendationChange('women', idx, 'garment', e.target.value)}
                                    placeholder="Garment name"
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-pink-500"
                                  />
                                  <input
                                    type="text"
                                    value={rec.reason || ''}
                                    onChange={(e) => handleGarmentRecommendationChange('women', idx, 'reason', e.target.value)}
                                    placeholder="Reason"
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-pink-500"
                                  />
                                  <input
                                    type="text"
                                    value={rec.silhouette || ''}
                                    onChange={(e) => handleGarmentRecommendationChange('women', idx, 'silhouette', e.target.value)}
                                    placeholder="Silhouette"
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-pink-500"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Men's Garments */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-medium text-blue-600">Men's Garments</p>
                              <button
                                onClick={() => handleAddGarmentRecommendation('men')}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                              >
                                <Plus size={12} />
                                Add
                              </button>
                            </div>
                            <div className="space-y-2">
                              {(editingFabricAnalysis.men_garment_recommendations?.best_suited || []).map((rec, idx) => (
                                <div key={idx} className="bg-blue-50 rounded p-2 space-y-2 border border-blue-200">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-blue-700">Recommendation #{idx + 1}</span>
                                    <button
                                      onClick={() => handleRemoveGarmentRecommendation('men', idx)}
                                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                                      title="Remove"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </div>
                                  <input
                                    type="text"
                                    value={rec.garment || ''}
                                    onChange={(e) => handleGarmentRecommendationChange('men', idx, 'garment', e.target.value)}
                                    placeholder="Garment name"
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                  <input
                                    type="text"
                                    value={rec.reason || ''}
                                    onChange={(e) => handleGarmentRecommendationChange('men', idx, 'reason', e.target.value)}
                                    placeholder="Reason"
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                  <input
                                    type="text"
                                    value={rec.silhouette || ''}
                                    onChange={(e) => handleGarmentRecommendationChange('men', idx, 'silhouette', e.target.value)}
                                    placeholder="Silhouette"
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : null}

                      {/* Design Guidelines */}
                      {isEditingFabricAnalysis ? (
                        <div className="bg-white/60 rounded-lg p-3 space-y-3">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Design Guidelines</p>
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">Ideal Garment Fit</label>
                            <input
                              type="text"
                              value={editingFabricAnalysis.design_guidelines?.ideal_garment_fit || ''}
                              onChange={(e) => handleFabricAnalysisFieldChange('design_guidelines', 'ideal_garment_fit', e.target.value)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., Loose, Fitted, Semi-fitted"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">Suggested Necklines (comma-separated)</label>
                            <input
                              type="text"
                              value={(editingFabricAnalysis.design_guidelines?.suggested_necklines || []).join(', ')}
                              onChange={(e) => handleArrayFieldChange('design_guidelines', 'suggested_necklines', e.target.value)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., V-neck, Round, Square"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">Suggested Sleeves (comma-separated)</label>
                            <input
                              type="text"
                              value={(editingFabricAnalysis.design_guidelines?.suggested_sleeves || []).join(', ')}
                              onChange={(e) => handleArrayFieldChange('design_guidelines', 'suggested_sleeves', e.target.value)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., Short, Long, Sleeveless"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">Ideal Length Options (comma-separated)</label>
                            <input
                              type="text"
                              value={(editingFabricAnalysis.design_guidelines?.ideal_length_options || []).join(', ')}
                              onChange={(e) => handleArrayFieldChange('design_guidelines', 'ideal_length_options', e.target.value)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., Short, Medium, Long"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">Print/Cutting Tips (comma-separated)</label>
                            <input
                              type="text"
                              value={(editingFabricAnalysis.design_guidelines?.print_or_cutting_tips || []).join(', ')}
                              onChange={(e) => handleArrayFieldChange('design_guidelines', 'print_or_cutting_tips', e.target.value)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., Cut on bias, Use pinking shears"
                            />
                          </div>
                        </div>
                      ) : null}

                      {/* Collapsed by default: Garment Recommendations + Design Guidelines (read-only) */}
                      {!isEditingFabricAnalysis &&
                        (
                          hasGarmentTypeInsights(localFabric.fabricAnalysis) ||
                          (localFabric.fabricAnalysis?.women_garment_recommendations?.best_suited?.length > 0) ||
                          (localFabric.fabricAnalysis?.men_garment_recommendations?.best_suited?.length > 0) ||
                          localFabric.fabricAnalysis?.design_guidelines
                        ) && (
                          <details className="rounded-lg border border-indigo-200/70 bg-white/50">
                            <summary className="cursor-pointer list-none px-3 py-2">
                              <div className="flex items-center justify-between gap-3">
                                <span className="text-xs font-semibold text-indigo-700">Recommendations &amp; guidelines</span>
                                <span className="text-[11px] font-medium text-slate-500">Collapsed by default</span>
                              </div>
                            </summary>
                            <div className="px-3 pb-3">
                              <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1 [scrollbar-width:thin]">
                                {hasGarmentTypeInsights(localFabric.fabricAnalysis) ? (
                                  <FabricGarmentInsights fabricAnalysis={localFabric.fabricAnalysis} compact />
                                ) : (
                                  (localFabric.fabricAnalysis?.women_garment_recommendations?.best_suited?.length > 0 ||
                                    localFabric.fabricAnalysis?.men_garment_recommendations?.best_suited?.length > 0) && (
                                    <div className="bg-white/60 rounded-lg p-2.5 space-y-3">
                                      <p className="text-xs font-semibold text-gray-700 mb-1">Garment Recommendations</p>

                                      {localFabric.fabricAnalysis.women_garment_recommendations?.best_suited?.length > 0 && (
                                        <div>
                                          <p className="text-xs font-medium text-pink-600 mb-1">Women&apos;s Garments:</p>
                                          <div className="space-y-2">
                                            {localFabric.fabricAnalysis.women_garment_recommendations.best_suited.slice(0, 5).map((rec, idx) => (
                                              <div key={idx} className="text-xs bg-pink-50 rounded p-2">
                                                <p className="font-medium text-gray-800">{rec.garment}</p>
                                                {rec.reason && <p className="text-gray-600 mt-0.5 line-clamp-2">{rec.reason}</p>}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {localFabric.fabricAnalysis.men_garment_recommendations?.best_suited?.length > 0 && (
                                        <div>
                                          <p className="text-xs font-medium text-blue-600 mb-1">Men&apos;s Garments:</p>
                                          <div className="space-y-2">
                                            {localFabric.fabricAnalysis.men_garment_recommendations.best_suited.slice(0, 5).map((rec, idx) => (
                                              <div key={idx} className="text-xs bg-blue-50 rounded p-2">
                                                <p className="font-medium text-gray-800">{rec.garment}</p>
                                                {rec.reason && <p className="text-gray-600 mt-0.5 line-clamp-2">{rec.reason}</p>}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )
                                )}

                                {localFabric.fabricAnalysis?.design_guidelines && (
                                  <div className="bg-white/60 rounded-lg p-2.5 space-y-2">
                                    <p className="text-xs font-semibold text-gray-700 mb-1">Design Guidelines</p>
                                    {localFabric.fabricAnalysis.design_guidelines.ideal_garment_fit && (
                                      <div>
                                        <span className="text-xs text-gray-600">Ideal Fit: </span>
                                        <span className="text-xs font-medium text-gray-800">
                                          {localFabric.fabricAnalysis.design_guidelines.ideal_garment_fit}
                                        </span>
                                      </div>
                                    )}
                                    {localFabric.fabricAnalysis.design_guidelines.suggested_necklines?.length > 0 && (
                                      <div>
                                        <span className="text-xs text-gray-600">Necklines: </span>
                                        <span className="text-xs font-medium text-gray-800">
                                          {localFabric.fabricAnalysis.design_guidelines.suggested_necklines.join(', ')}
                                        </span>
                                      </div>
                                    )}
                                    {localFabric.fabricAnalysis.design_guidelines.suggested_sleeves?.length > 0 && (
                                      <div>
                                        <span className="text-xs text-gray-600">Sleeves: </span>
                                        <span className="text-xs font-medium text-gray-800">
                                          {localFabric.fabricAnalysis.design_guidelines.suggested_sleeves.join(', ')}
                                        </span>
                                      </div>
                                    )}
                                    {localFabric.fabricAnalysis.design_guidelines.ideal_length_options?.length > 0 && (
                                      <div>
                                        <span className="text-xs text-gray-600">Length Options: </span>
                                        <span className="text-xs font-medium text-gray-800">
                                          {localFabric.fabricAnalysis.design_guidelines.ideal_length_options.join(', ')}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </details>
                        )}
                    </div>
                  )}

                  {/* Add Fabric Analysis Button (if not exists) */}
                  {!localFabric.fabricAnalysis && !isEditingFabricAnalysis && canEditFabricAnalysis && (
                    <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-lg border-2 border-dashed border-indigo-300 p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Sparkles size={18} className="text-indigo-600" />
                          <div>
                            <p className="text-sm font-semibold text-indigo-700">Fabric Analysis</p>
                            <p className="text-xs text-gray-600">No analysis data available</p>
                          </div>
                        </div>
                        <button
                          onClick={handleStartEditFabricAnalysis}
                          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                        >
                          <Plus size={16} />
                          Add Analysis
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Connect with Vendor */}
                  {!liteMode && !isOwner && resolveVendorId() && (
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <button
                        type="button"
                        onClick={handleConnectWithVendor}
                        disabled={isSubmittingVendorConnect}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-900 hover:bg-black text-white rounded-lg font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isSubmittingVendorConnect ? (
                          <>
                            <Loader2 size={18} className="animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <MessageCircle size={18} />
                            Connect with vendor
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Tags Section */}
                  {(!liteMode || (localFabric.tags && localFabric.tags.length > 0)) && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-semibold text-gray-700">Tags</p>
                      {canEditTags && !isEditingTags && (
                        <button
                          onClick={handleStartEditTags}
                          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          <Edit2 size={14} />
                          Edit
                        </button>
                      )}
                    </div>

                    {isEditingTags ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Press Enter or comma to add a tag</span>
                          <span className="text-xs text-gray-500">{editingTags.length}/{MAX_TAGS_PER_FABRIC}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <div className="relative flex-1">
                            <input
                              type="text"
                              value={tagInput}
                              onChange={(e) => setTagInput(e.target.value)}
                              onKeyDown={handleTagKeyDown}
                              placeholder="Add tags (e.g., cotton, summer, formal)"
                              className="w-full px-3 py-2 pr-9 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              disabled={editingTags.length >= MAX_TAGS_PER_FABRIC}
                            />
                            <Tag size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                          </div>
                          <button
                            type="button"
                            onClick={handleTagAdd}
                            disabled={!tagInput.trim() || editingTags.length >= MAX_TAGS_PER_FABRIC}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Add Tag
                          </button>
                        </div>
                        {editingTags.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {editingTags.map((tag) => (
                              <span
                                key={tag}
                                className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium"
                              >
                                {tag}
                                <button
                                  type="button"
                                  onClick={() => handleTagRemove(tag)}
                                  className="text-blue-600 hover:text-blue-800 rounded-full p-0.5"
                                  aria-label={`Remove tag ${tag}`}
                                >
                                  <X size={12} />
                                </button>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500 italic">No tags added yet</p>
                        )}
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={handleCancelEditTags}
                            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            disabled={isSavingTags}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveTags}
                            disabled={isSavingTags}
                            className="flex items-center gap-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isSavingTags ? (
                              <>
                                <Loader2 size={14} className="animate-spin" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save size={14} />
                                Save
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {localFabric.tags && localFabric.tags.length > 0 ? (
                          localFabric.tags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"
                            >
                              {tag}
                            </span>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 italic">No tags added yet</p>
                        )}
                      </div>
                    )}
                  </div>
                  )}

                  {/* Properties Section */}
                  {(!liteMode || (localFabric.properties && localFabric.properties.length > 0)) && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-semibold text-gray-700">Properties</p>
                      {canEditProperties && !isEditingProperties && (
                        <button
                          onClick={handleStartEditProperties}
                          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          <Edit2 size={14} />
                          Edit
                        </button>
                      )}
                    </div>

                    {isEditingProperties ? (
                      <div className="space-y-3">
                        {editingProperties.map((prop, index) => (
                          <div key={index} className="flex gap-2">
                            <input
                              type="text"
                              placeholder="Property name"
                              value={prop.key || ''}
                              onChange={(e) => handlePropertyChange(index, 'key', e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                            <input
                              type="text"
                              placeholder="Property value"
                              value={prop.value || ''}
                              onChange={(e) => handlePropertyChange(index, 'value', e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                            <button
                              onClick={() => handleRemoveProperty(index)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove property"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <button
                            onClick={handleAddProperty}
                            className="flex items-center gap-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Plus size={14} />
                            Add Property
                          </button>
                          <div className="flex-1" />
                          <button
                            onClick={handleCancelEditProperties}
                            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            disabled={isSavingProperties}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveProperties}
                            disabled={isSavingProperties}
                            className="flex items-center gap-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isSavingProperties ? (
                              <>
                                <Loader2 size={14} className="animate-spin" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save size={14} />
                                Save
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {localFabric.properties && localFabric.properties.length > 0 ? (
                          localFabric.properties.map((prop, index) => (
                            <div key={index} className="flex items-center gap-2 text-sm">
                              <span className="font-medium text-gray-700">{prop.key}:</span>
                              <span className="text-gray-600">{prop.value}</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 italic">No properties added yet</p>
                        )}
                      </div>
                    )}
                  </div>
                  )}
                </div>
              </div>
            </div>

            {/* Shade Cards Section - stable layout; keep slots even if an image fails */}
            {shadeCards.length > 0 && (
              <div className="bg-white rounded-2xl border-2 border-purple-200 shadow-lg p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shrink-0">
                    <ImageIcon size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">Shade Cards</h3>
                    <p className="text-sm text-gray-600">
                      {shadeCards.length} shade card{shadeCards.length > 1 ? 's' : ''} available
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {shadeCards.map((shadeCard, index) => {
                    const shadeCardUrl = typeof shadeCard === 'string' ? shadeCard : shadeCard.url;
                    if (!shadeCardUrl) return null;
                    const broken = isImageBroken(shadeCardUrl);
                    return (
                      <div
                        key={shadeCardUrl}
                        className="group relative bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl overflow-hidden border-2 border-purple-200 md:hover:border-purple-400 md:hover:shadow-xl transition-[border-color,box-shadow] cursor-pointer"
                        onClick={() => !broken && handleImageClick(shadeCardUrl)}
                      >
                        <div className="relative aspect-video min-h-[140px] bg-gray-100">
                          {broken ? (
                            <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 p-4">
                              <ImageIcon size={32} className="mb-2 opacity-50" />
                              <p className="text-xs text-center">Preview unavailable</p>
                            </div>
                          ) : (
                            <img
                              {...imageInteractionProps}
                              src={shadeCardUrl}
                              alt={`Shade card ${index + 1}`}
                              className="w-full h-full object-cover md:group-hover:scale-105 transition-transform duration-300"
                              loading="lazy"
                              decoding="async"
                              onError={() => handleImageError(shadeCardUrl)}
                            />
                          )}
                          <div className="absolute top-2 left-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg z-10 pointer-events-none">
                            Shade Card #{index + 1}
                          </div>
                          {!broken && (
                            <div className="absolute inset-0 hidden md:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 pointer-events-none">
                              <div className="bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg">
                                <p className="text-sm font-semibold text-gray-800">Click to view full size</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Upload Shade Card Section - Only show if no shade cards exist and uploads are allowed */}
            {canUploadShadeCards && shadeCards.length === 0 && (
              <div className="bg-white rounded-2xl border-2 border-dashed border-purple-300 shadow-lg p-6">
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ImageIcon size={32} className="text-purple-500" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Upload Shade Card</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Upload a shade card image to extract color variants for this fabric
                  </p>
                  <button
                    onClick={() => shadeCardInputRef.current?.click()}
                    disabled={isUploadingShadeCard || !localFabric._id}
                    className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                      isUploadingShadeCard || !localFabric._id
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-lg'
                    }`}
                  >
                    {isUploadingShadeCard ? (
                      <>
                        <Loader2 size={20} className="animate-spin" />
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload size={20} />
                        <span>Choose Shade Card Image</span>
                      </>
                    )}
                  </button>
                  <input
                    ref={shadeCardInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleShadeCardFileChange}
                    className="hidden"
                  />
                </div>
              </div>
            )}

            {/* Color Variants Section — min-height avoids jump when palette appears */}
            {(normalizedColorVariants && normalizedColorVariants.length > 0) || (isEditingColors && editingColorVariants.length > 0) ? (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6 min-h-[120px]">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                      <Palette size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">Color Palette</h3>
                      <p className="text-sm text-gray-600">
                        {isEditingColors 
                          ? `${editingColorVariants.length} color${editingColorVariants.length > 1 ? 's' : ''}`
                          : `${normalizedColorVariants.length} color${normalizedColorVariants.length > 1 ? 's' : ''} extracted from shade card`
                        }
                      </p>
                    </div>
                  </div>
                  {canEditColors && !isEditingColors && (
                    <button
                      onClick={handleStartEditColors}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      <Edit2 size={16} />
                      Edit Colors
                    </button>
                  )}
                  {isEditingColors && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleCancelEditColors}
                        disabled={isSavingColors}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveColors}
                        disabled={isSavingColors}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
                      >
                        {isSavingColors ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save size={16} />
                            Save
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {isEditingColors ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {editingColorVariants.map((variant, index) => {
                        const colorName = variant.colorName || `Color ${index + 1}`;
                        const hex = variant.hex || '#000000';
                        
                        return (
                          <div
                            key={index}
                            className="bg-gray-50 rounded-xl border-2 border-gray-200 p-4 space-y-3"
                          >
                            <div
                              className="aspect-square w-full rounded-lg border-2 border-gray-300"
                              style={{ backgroundColor: hex }}
                            />
                            <div className="space-y-2">
                              <div>
                                <label className="text-xs font-medium text-gray-700 mb-1 block">
                                  Color Name
                                </label>
                                <input
                                  type="text"
                                  value={colorName}
                                  onChange={(e) => handleColorNameChange(index, e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Enter color name"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-medium text-gray-700 mb-1 block">
                                  Hex Code
                                </label>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={hex}
                                    onChange={(e) => handleColorHexChange(index, e.target.value)}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="#000000"
                                    maxLength={7}
                                  />
                                  <button
                                    onClick={() => handleRemoveColor(index)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                    title="Remove color"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <button
                      onClick={handleAddColor}
                      className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-blue-400 hover:text-blue-600 transition-colors text-sm font-medium"
                    >
                      <Plus size={16} />
                      Add New Color
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                    {normalizedColorVariants.map((variant, index) => {
                      const colorName = variant.colorName || `Color ${index + 1}`;
                      const hex = variant.hex || '#000000';
                      
                      return (
                        <div
                          key={index}
                          className="group relative bg-white rounded-xl border-2 border-gray-200 md:hover:border-purple-300 md:hover:shadow-md transition-[border-color,box-shadow] overflow-hidden"
                          title={`${colorName} - ${hex}`}
                        >
                          <div
                            className="aspect-square w-full"
                            style={{ backgroundColor: hex }}
                          />
                          <div className="p-2 bg-white">
                            <p className="text-xs font-semibold text-gray-800 text-center truncate mb-0.5" title={colorName}>
                              {colorName}
                            </p>
                            <p className="text-[10px] font-mono text-gray-500 text-center truncate" title={hex}>
                              {hex}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : canEditColors && !isEditingColors ? (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                      <Palette size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">Color Palette</h3>
                      <p className="text-sm text-gray-600">No colors extracted yet</p>
                    </div>
                  </div>
                  <button
                    onClick={handleStartEditColors}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    <Plus size={16} />
                    Add Colors
                  </button>
                </div>
              </div>
            ) : null}

            {/* Generated Kits Section */}
            <div data-fabric-kits-section className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
              {!liteMode && (
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                  <Package size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Generated Kits</h3>
                  <p className="text-sm text-gray-600">
                    {isLoadingKits ? 'Loading...' : `${kits.length} kit${kits.length !== 1 ? 's' : ''} created from this fabric`}
                  </p>
                </div>
              </div>
              )}

              {isLoadingKits ? (
                <div className="flex items-center justify-center py-12 min-h-[200px]">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              ) : kits.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                  <Package size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600 mb-2">No kits generated yet</p>
                  <p className="text-sm text-gray-500">Create your first kit using this fabric</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {kits.map((kit) => {
                    const kitImage = kit.generatedImageUrl || kit.productDetails?.image;
                    if (!kitImage) return null;
                    return (
                    <div
                      key={kit._id}
                      className="group bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 hover:border-purple-300 hover:shadow-lg transition-all overflow-hidden cursor-pointer"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleKitCardClick(kit, kitImage)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleKitCardClick(kit, kitImage);
                        }
                      }}
                      title={openAiStudio ? 'Open kit in Studio' : 'View kit image'}
                    >
                      {/* Kit Image */}
                      <div 
                        className="relative aspect-[3/4] bg-gray-100"
                      >
                        <img
                          {...imageInteractionProps}
                          src={kitImage}
                          alt={kit.name}
                          className="w-full h-full object-contain md:group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                          decoding="async"
                          onError={() => handleImageError(kitImage)}
                        />
                        {kit.status === 'generated' && (
                          <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                            ✓ Generated
                          </div>
                        )}
                      </div>

                      {/* Kit Details */}
                      <div className="p-4">
                        <h4 className="font-semibold text-gray-800 mb-1 truncate">{kit.name}</h4>
                        <p className="text-xs text-gray-500 mb-2">
                          {new Date(kit.createdAt || kit.dateGenerated).toLocaleDateString()}
                        </p>

                        {/* Color Variants */}
                        {kit.generatedColorVariants && kit.generatedColorVariants.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-gray-600 mb-2 font-medium">Color Variants:</p>
                            <div className="flex flex-wrap gap-2">
                              {kit.generatedColorVariants.slice(0, 6).filter(variant => 
                                !variant.generatedImageUrl || !isImageBroken(variant.generatedImageUrl)
                              ).map((variant, vIndex) => (
                                <div
                                  key={vIndex}
                                  className="relative group/variant cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (variant.generatedImageUrl && !isImageBroken(variant.generatedImageUrl)) {
                                      handleImageClick(variant.generatedImageUrl);
                                    }
                                  }}
                                  title={variant.colorName || variant.hex}
                                >
                                  {variant.generatedImageUrl && !isImageBroken(variant.generatedImageUrl) ? (
                                    <img
                                      {...imageInteractionProps}
                                      src={variant.generatedImageUrl}
                                      alt={variant.colorName || variant.hex}
                                      className="w-10 h-10 rounded-lg border-2 border-gray-200 hover:border-purple-400 object-cover transition-all"
                                      onError={() => handleImageError(variant.generatedImageUrl)}
                                    />
                                  ) : (
                                    <div
                                      className="w-10 h-10 rounded-lg border-2 border-gray-200 hover:border-purple-400 transition-all"
                                      style={{ backgroundColor: variant.hex || '#ccc' }}
                                    />
                                  )}
                                </div>
                              ))}
                              {kit.generatedColorVariants.length > 6 && (
                                <div className="w-10 h-10 rounded-lg border-2 border-gray-200 bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-600">
                                  +{kit.generatedColorVariants.length - 6}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="mt-3 space-y-2">
                          {/* View/Generate Color Variants Button - Only show if kit has generated image */}
                          {kit.generatedImageUrl && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewColorVariants(kit);
                              }}
                              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl active:scale-95"
                              title="View and Generate Color Variants"
                            >
                              <Palette size={16} />
                              <span>Color Variants</span>
                              {kit.generatedColorVariants && kit.generatedColorVariants.length > 0 && (
                                <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                                  {kit.generatedColorVariants.length}
                                </span>
                              )}
                            </button>
                          )}

                          {/* Edit in Studio Button */}
                          {openAiStudio && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openAiStudio(kit);
                              }}
                              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl active:scale-95"
                              title="Edit in Studio"
                            >
                              <Sparkles size={16} />
                              <span>Edit in Studio</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Full Screen Image Viewer */}
      {selectedImage && !isImageBroken(selectedImage) && (
        <div 
          className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={handleCloseImage}
        >
          {/* Close Button */}
          <button
            onClick={handleCloseImage}
            className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors backdrop-blur-sm z-10"
            aria-label="Close"
          >
            <X size={24} />
          </button>

          {/* Navigation Buttons */}
          {allViewableImages.length > 1 && selectedImageIndex >= 0 && (
            <>
              {selectedImageIndex > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreviousImage();
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors backdrop-blur-sm z-10"
                  aria-label="Previous image"
                >
                  <ArrowLeft size={24} />
                </button>
              )}
              {selectedImageIndex < allViewableImages.length - 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNextImage();
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors backdrop-blur-sm z-10"
                  aria-label="Next image"
                >
                  <ArrowLeft size={24} className="rotate-180" />
                </button>
              )}
              {/* Image Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-medium z-10">
                {selectedImageIndex + 1} / {allViewableImages.length}
              </div>
            </>
          )}

          {/* Image */}
          <img
            {...imageInteractionProps}
            src={selectedImage}
            alt="Full view"
            className="max-w-full max-h-full object-contain cursor-zoom-out"
            onClick={(e) => e.stopPropagation()}
            onError={() => {
              handleImageError(selectedImage);
              handleCloseImage();
            }}
          />

          {/* Instructions */}
          {allViewableImages.length > 1 && (
            <div className="absolute top-20 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg text-white text-xs text-center z-10">
              Use arrow keys or buttons to navigate • Press ESC to close
            </div>
          )}
        </div>
      )}

      {/* Color Variants Modal */}
      {colorVariantsModalOpen && selectedKitForColorVariants && (
        <ColorVariantsModal
          isOpen={colorVariantsModalOpen}
          onClose={handleCloseColorVariantsModal}
          kitId={selectedKitForColorVariants._id}
          showNotification={showNotification}
          refreshKits={handleRefreshKits}
          fabricColorVariants={normalizedColorVariants}
        />
      )}

      {/* Connect with vendor modal (for non-auth users) */}
      {showConnectVendorModal && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Connect with vendor"
          onClick={() => {
            if (!isSubmittingVendorConnect) setShowConnectVendorModal(false);
          }}
        >
          <div
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <p className="text-lg font-bold text-gray-900">Connect with vendor</p>
                <p className="text-xs text-gray-600 mt-0.5">Share your details and we’ll notify the vendor.</p>
              </div>
              <button
                type="button"
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-700"
                onClick={() => setShowConnectVendorModal(false)}
                disabled={isSubmittingVendorConnect}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-5 py-4 space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Name (optional)</label>
                <input
                  value={connectRequesterName}
                  onChange={(e) => setConnectRequesterName(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-900 text-sm"
                  placeholder="Your name"
                  disabled={isSubmittingVendorConnect}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Mobile number</label>
                <input
                  value={connectMobileNumber}
                  onChange={(e) => setConnectMobileNumber(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-900 text-sm"
                  placeholder="10-digit mobile number"
                  inputMode="numeric"
                  disabled={isSubmittingVendorConnect}
                />
              </div>
            </div>

            <div className="px-5 py-4 border-t border-gray-200 flex items-center gap-3">
              <button
                type="button"
                className="flex-1 px-4 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={() => setShowConnectVendorModal(false)}
                disabled={isSubmittingVendorConnect}
              >
                Cancel
              </button>
              <button
                type="button"
                className="flex-1 px-4 py-3 rounded-xl bg-gray-900 hover:bg-black text-white font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                onClick={() => {
                  const trimmedMobile = String(connectMobileNumber || '').trim();
                  if (!trimmedMobile) {
                    showNotification?.('Mobile number is required', 'error');
                    return;
                  }
                  submitVendorConnectRequest({
                    requesterName: connectRequesterName,
                    mobileNumber: trimmedMobile
                  });
                }}
                disabled={isSubmittingVendorConnect}
              >
                {isSubmittingVendorConnect ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FabricCatalogueView;

